
import React, { useEffect, useState } from 'react';
import { BlockHeader, BlockTitle, StyledUserItem, UserBlockForm, UserNameRow } from '../Permissions.styles';
import { useTranslation } from 'react-i18next';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { PermissionEntityType } from '../../../types/permissionType';
import { Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, TextField } from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { useAppDispatch } from '../../../hooks/StoreHooks';
import { createCompanyManagementUserThunk, deleteCompanyManagementUserThunk, editCompanyManagementUserThunk } from '../../../store/thunk/companyManagementThunk';
import { GeneralButton } from '../../../common/ui/GeneralButton';
import { useProfileData } from '../../../store/selectors/profileSelector';
import { fetchProfileThunk } from '../../../store/thunk/profileThunk';
import { UserManagementErrorCode } from "../../../api/userManagementErrorCode";

type CompanyManagementUser = {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    emailConfirmed: boolean;
};

type UserListBlockProps = {
    users: CompanyManagementUser[];
    selectedUserId: string | null;
    onUserClick: (user: CompanyManagementUser) => void;
};

const UserListBlock: React.FC<UserListBlockProps> = ({
    users,
    selectedUserId,
    onUserClick,
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { userHasPermission } = usePermissionChecker();
    const profile = useProfileData();
    const currentUserId = profile?.id;

    useEffect(() => {
        if (!profile) {
            dispatch(fetchProfileThunk());
        }
    }, [dispatch, profile]);

    const canCreateCMU = userHasPermission(PermissionEntityType.CompanyManagementUsers, 'Create');
    const canEditCMU = userHasPermission(PermissionEntityType.CompanyManagementUsers, 'Edit');
    const canDeleteCMU = userHasPermission(PermissionEntityType.CompanyManagementUsers, 'Delete');

    const [emailError, setEmailError] = useState<string | null>(null);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [addFormData, setAddFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
    });
    const isAddDisabled = !addFormData.firstName || !addFormData.lastName || !addFormData.email;

    const handleOpenAddDialog = () => setAddDialogOpen(true);

    const handleCloseAddDialog = () => {
        setAddDialogOpen(false);
        setEmailError(null);
        resetAddForm();
    };

    const handleAddChange = (field: keyof typeof addFormData) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (field === 'email') setEmailError(null);
            setAddFormData(prev => ({ ...prev, [field]: event.target.value }));
        };

    const handleAddSubmit = async () => {
        setEmailError(null);

        const resultAction = await dispatch(createCompanyManagementUserThunk({
            firstName: addFormData.firstName,
            lastName: addFormData.lastName,
            email: addFormData.email,
        }));

        if (createCompanyManagementUserThunk.rejected.match(resultAction)) {
            const errors = resultAction.payload?.data?.errors as { code: string }[] | undefined;
            const isDuplicateEmail = errors?.some(
                (e) => e.code === UserManagementErrorCode.DuplicateUserName
            );

            if (isDuplicateEmail) {
                setEmailError(t("errorCode.UserEmailAlreadyTaken"));
                return;
            }
            return;
        }

        handleCloseAddDialog();
    };

    const resetAddForm = () =>
        setAddFormData({ firstName: '', lastName: '', email: '' });

    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editFormData, setEditFormData] = useState({
        id: '',
        firstName: '',
        lastName: '',
        email: '',
    })
    const isEditDisabled = !editFormData.firstName || !editFormData.lastName;

    const handleOpenEditDialog = (user: CompanyManagementUser) => {
        setEditFormData({
            id: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
        });
        setEditDialogOpen(true);
    };

    const handleEditChange = (field: keyof typeof editFormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setEditFormData(prev => ({ ...prev, [field]: event.target.value }));
    };

    const handleEditSubmit = async () => {
        await dispatch(editCompanyManagementUserThunk({
            id: editFormData.id,
            firstName: editFormData.firstName,
            lastName: editFormData.lastName,
        }));

        setEditDialogOpen(false);
    };

    const handleDeleteUser = async () => {
        if (editFormData.id === currentUserId) {
            return;
        }
        await dispatch(deleteCompanyManagementUserThunk(editFormData.id));
        setEditDialogOpen(false);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
    };

    return (
        <>
            <BlockHeader>
                <BlockTitle>{t('permissionsPage.usersTitle')}</BlockTitle>
                {canCreateCMU && (
                    <IconButton onClick={handleOpenAddDialog} sx={{ color: theme => theme.palette.primary.main }}>
                        <Add />
                    </IconButton>
                )}
            </BlockHeader>

            {users.map((user) => (
                <UserNameRow key={user.id} selected={user.id === selectedUserId} onClick={() => onUserClick(user)}>
                    <StyledUserItem>
                        {user.firstName} {user.lastName}
                    </StyledUserItem>
                    {canEditCMU && (
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEditDialog(user);
                            }}
                            sx={{ color: theme => theme.palette.primary.main }}
                            size="small"
                        >
                            <Edit fontSize="small" />
                        </IconButton>
                    )}
                </UserNameRow>
            ))}

            <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} fullWidth maxWidth="sm">
                <DialogTitle>{t('permissionsPage.addCMU')}</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth>
                        <UserBlockForm>
                            <TextField
                                label={t('authentication.register.firstName')}
                                value={addFormData.firstName}
                                onChange={handleAddChange('firstName')}
                            />
                            <TextField
                                label={t('authentication.register.lastName')}
                                value={addFormData.lastName}
                                onChange={handleAddChange('lastName')}
                            />
                            <TextField
                                label={t('authentication.register.email')}
                                value={addFormData.email}
                                onChange={handleAddChange('email')}
                                error={!!emailError}
                                helperText={emailError ?? ''}
                            />
                        </UserBlockForm>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <GeneralButton
                        appVariant="secondary"
                        onClick={handleCloseAddDialog}
                    >
                        {t('buttons.cancel')}
                    </GeneralButton>
                    <GeneralButton
                        onClick={handleAddSubmit}
                        disabled={isAddDisabled}
                    >
                        {t('buttons.ok')}
                    </GeneralButton>
                </DialogActions>
            </Dialog>

            <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} fullWidth maxWidth="sm">
                <DialogTitle>{t('permissionsPage.editCMU')}</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth>
                        <UserBlockForm>
                            <TextField
                                label={t('authentication.register.firstName')}
                                value={editFormData.firstName}
                                onChange={handleEditChange('firstName')}
                            />
                            <TextField
                                label={t('authentication.register.lastName')}
                                value={editFormData.lastName}
                                onChange={handleEditChange('lastName')}
                            />
                            <TextField
                                label={t('authentication.register.email')}
                                value={editFormData.email}
                                disabled
                            />
                        </UserBlockForm>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <DialogActions>
                        {canDeleteCMU && (
                            <GeneralButton
                                appVariant="danger"
                                onClick={handleDeleteUser}
                                disabled={editFormData.id === currentUserId}
                            >
                                {t('buttons.delete')}
                            </GeneralButton>
                        )}
                        <GeneralButton
                            appVariant="secondary"
                            onClick={handleCloseEditDialog}
                        >
                            {t('buttons.cancel')}
                        </GeneralButton>
                        <GeneralButton
                            appVariant="primary"
                            onClick={handleEditSubmit}
                            disabled={isEditDisabled}
                        >
                            {t('buttons.ok')}
                        </GeneralButton>
                    </DialogActions>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UserListBlock;
